/**
 * AJ2 · Home — hero slider, tabs de soluciones y añadir al carrito.
 *
 * Todo es mejora progresiva: sin JavaScript el hero enseña el primer slide,
 * los paneles de soluciones se apilan con su nombre visible y las tarjetas
 * enlazan a la ficha.
 */

/* ---------- Hero slider ----------
 * v2 (28/09/2026): autoplay y tiempo desde la sección, los controles toman
 * el tono del slide activo, se desliza con el dedo, los slides ocultos son
 * inertes (el tabulador no entra en botones que no se ven) y, en el
 * personalizador, al seleccionar un slide se salta a él y el autoplay se
 * para hasta que se deselecciona. */
function iniciarHero(raiz) {
  const pista = raiz.querySelector('[data-aj2-pista]');
  const slides = [...raiz.querySelectorAll('.aj2-hero__slide')];
  const total = slides.length;
  if (!pista || !total) return;

  let actual = 0;
  let temporizador = null;
  let fijado = false;
  const autoplay = raiz.dataset.aj2Autoplay !== 'false';
  const segundos = Math.max(3, Number(raiz.dataset.aj2Velocidad) || 6);
  const sinMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const contador = raiz.querySelector('[data-aj2-hero-actual]');
  const relleno = raiz.querySelector('.aj2-hero__progreso-relleno');
  if (relleno) relleno.style.width = 100 / total + '%';

  const pintar = () => {
    pista.style.transform = `translateX(-${actual * 100}%)`;
    if (contador) contador.textContent = String(actual + 1);
    if (relleno) relleno.style.transform = `translateX(${actual * 100}%)`;
    raiz.dataset.tono = slides[actual].dataset.tono || 'oscuro';
    slides.forEach((slide, i) => {
      const visible = i === actual;
      slide.inert = !visible;
      slide.setAttribute('aria-hidden', String(!visible));
    });
  };
  const ir = (n) => {
    actual = (n + total) % total;
    pintar();
  };
  const parar = () => {
    if (temporizador) clearInterval(temporizador);
    temporizador = null;
  };
  const reprogramar = () => {
    parar();
    /* el personalizador recarga la sección y deja este nodo suelto */
    if (!raiz.isConnected) {
      document.removeEventListener('visibilitychange', reprogramar);
      return;
    }
    if (!autoplay || sinMovimiento || fijado || total < 2 || document.hidden) return;
    temporizador = window.setInterval(() => {
      if (!raiz.isConnected) {
        reprogramar();
        return;
      }
      ir(actual + 1);
    }, segundos * 1000);
  };

  raiz.querySelector('[data-aj2-hero-prev]')?.addEventListener('click', () => { ir(actual - 1); reprogramar(); });
  raiz.querySelector('[data-aj2-hero-next]')?.addEventListener('click', () => { ir(actual + 1); reprogramar(); });

  /* deslizar con el dedo: solo gestos claramente horizontales */
  if (total > 1) {
    let x0 = null;
    let y0 = null;
    pista.addEventListener('touchstart', (e) => {
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
    }, { passive: true });
    pista.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      const dy = e.changedTouches[0].clientY - y0;
      x0 = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      ir(actual + (dx < 0 ? 1 : -1));
      reprogramar();
    }, { passive: true });
  }

  /* sin pestaña visible no se gira: al volver se retoma */
  document.addEventListener('visibilitychange', reprogramar);

  /* personalizador: el slide seleccionado se enseña y se queda quieto */
  raiz.addEventListener('shopify:block:select', (e) => {
    const i = slides.indexOf(e.target.closest('.aj2-hero__slide'));
    if (i < 0) return;
    fijado = true;
    parar();
    ir(i);
  });
  raiz.addEventListener('shopify:block:deselect', () => {
    fijado = false;
    reprogramar();
  });

  pintar();
  reprogramar();
}

/* ---------- Tabs de soluciones ---------- */
function iniciarSoluciones(raiz) {
  const paneles = [...raiz.querySelectorAll('[data-aj2-panel]')];
  if (!paneles.length) return;
  const rail = raiz.querySelector('[data-aj2-tabs]');
  if (!rail) return;

  rail.hidden = false;
  paneles.forEach((p, i) => {
    const boton = document.createElement('button');
    boton.type = 'button';
    boton.className = 'aj2-sol__tab';
    boton.setAttribute('role', 'tab');
    boton.setAttribute('aria-selected', String(i === 0));
    boton.textContent = p.dataset.aj2Panel;
    boton.addEventListener('click', () => {
      paneles.forEach((q, j) => { q.hidden = q !== p; rail.children[j].setAttribute('aria-selected', String(q === p)); });
    });
    rail.appendChild(boton);
    /* con las tabs activas, el rótulo propio del panel sobra */
    const rotulo = p.querySelector('[data-aj2-panel-rotulo]');
    if (rotulo) rotulo.hidden = true;
    p.hidden = i !== 0;
  });
}

/* ---------- Añadir al carrito desde la tarjeta ---------- */
function iniciarAnadir(raiz) {
  for (const boton of raiz.querySelectorAll('[data-aj2-anadir]')) {
    if (boton.dataset.aj2Listo) continue;
    boton.dataset.aj2Listo = '1';
    boton.addEventListener('click', async () => {
      boton.disabled = true;
      try {
        const respuesta = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(boton.dataset.aj2Anadir), quantity: 1 }] }),
        });
        if (!respuesta.ok) throw new Error('cart/add');
        window.location.reload();
      } catch (error) {
        boton.disabled = false;
      }
    });
  }
}

/* ---------- Modal con formulario embebido (Typeform) ----------
 *
 * El script de Typeform no entra en la home: se pide en el primer clic. Y el
 * <div data-tf-live> se inyecta ya con el modal abierto, porque embed.js NO
 * observa el DOM — solo escanea cuando se lo pides. tf.load() re-escanea y no
 * duplica lo que ya está pintado, así que se puede llamar siempre.
 */
const AJ2_TYPEFORM_SRC = 'https://embed.typeform.com/next/embed.js';
let aj2PromesaTypeform = null;

function cargarTypeform() {
  if (window.tf) return Promise.resolve();
  if (aj2PromesaTypeform) return aj2PromesaTypeform;
  aj2PromesaTypeform = new Promise((resolver, rechazar) => {
    const script = document.createElement('script');
    script.src = AJ2_TYPEFORM_SRC;
    script.async = true;
    script.addEventListener('load', resolver, { once: true });
    script.addEventListener(
      'error',
      () => {
        aj2PromesaTypeform = null;
        rechazar(new Error('typeform'));
      },
      { once: true }
    );
    document.head.appendChild(script);
  });
  return aj2PromesaTypeform;
}

function montarFormulario(cuerpo) {
  if (!cuerpo || cuerpo.dataset.aj2Montado) return;
  const id = cuerpo.dataset.aj2Typeform;
  if (!id) return;
  cuerpo.dataset.aj2Montado = '1';

  const hueco = document.createElement('div');
  hueco.className = 'aj2-modal__form';
  hueco.setAttribute('data-tf-live', id);
  cuerpo.appendChild(hueco);

  cargarTypeform()
    .then(() => {
      if (window.tf && typeof window.tf.load === 'function') window.tf.load();
    })
    .catch(() => {
      delete cuerpo.dataset.aj2Montado;
      hueco.remove();
      const aviso = document.createElement('p');
      aviso.className = 'aj2-modal__error';
      aviso.textContent =
        cuerpo.dataset.aj2Error ||
        'No hemos podido cargar el formulario. Vuelve a intentarlo o escríbenos y te damos cita igualmente.';
      cuerpo.appendChild(aviso);
    });
}

/* Horizon no scrollea el documento: pone overflow:hidden en html y body y
 * mueve el scroll a .page-wrapper. Quién congela es el CSS (una sola clase),
 * no una heurística en JS: así no hay estado que restaurar ni forma de dejar
 * la pagina bloqueada si algo falla a mitad. */
function bloquearScroll(bloquear) {
  document.documentElement.classList.toggle('aj2-sin-scroll', bloquear);
}

function abrirModal(modal, disparador) {
  if (!modal || modal.open) return;
  if (disparador) modal.dataset.aj2Volver = disparador.id || '';
  modal.aj2Disparador = disparador || null;
  if (typeof modal.showModal === 'function') {
    modal.showModal();
  } else {
    modal.setAttribute('open', '');
  }
  bloquearScroll(true);
  montarFormulario(modal.querySelector('[data-aj2-typeform]'));
}

function cerrarModal(modal) {
  if (!modal || !modal.open) return;
  const fin = () => {
    modal.classList.remove('is-cerrando');
    if (typeof modal.close === 'function') {
      modal.close();
    } else {
      modal.removeAttribute('open');
    }
    bloquearScroll(false);
    const volver = modal.aj2Disparador;
    if (volver && document.contains(volver)) volver.focus();
  };
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    fin();
    return;
  }
  modal.classList.add('is-cerrando');
  window.setTimeout(fin, 160);
}

function iniciarModales() {
  if (document.documentElement.dataset.aj2Modales) return;
  document.documentElement.dataset.aj2Modales = '1';

  document.addEventListener('click', (evento) => {
    const abre = evento.target.closest('[data-aj2-modal-abre]');
    if (abre) {
      const modal = document.getElementById(abre.dataset.aj2ModalAbre);
      if (modal) {
        evento.preventDefault();
        abrirModal(modal, abre);
      }
      return;
    }
    const cierra = evento.target.closest('[data-aj2-modal-cierra]');
    if (cierra) {
      evento.preventDefault();
      cerrarModal(cierra.closest('.aj2-modal'));
      return;
    }
    /* clic en el fondo: el <dialog> ocupa toda la pantalla, el panel no */
    if (evento.target.classList && evento.target.classList.contains('aj2-modal')) {
      cerrarModal(evento.target);
    }
  });

  /* Escape lo cierra el propio <dialog>: solo hay que soltar el scroll */
  document.addEventListener('close', (evento) => {
    if (evento.target.classList && evento.target.classList.contains('aj2-modal')) {
      bloquearScroll(false);
    }
  }, true);
}

/* Los modales se sacan al <body> para que ningún ancestro con transform,
 * filter o contain pueda recortarlos. Idempotente: al recargar la sección en
 * el personalizador, el nuevo sustituye al que hubiera colgado antes. */
function portarModales() {
  for (const modal of document.querySelectorAll('.aj2-modal')) {
    if (modal.parentElement === document.body) continue;
    if (modal.id) {
      for (const viejo of document.body.children) {
        if (viejo !== modal && viejo.id === modal.id && viejo.classList.contains('aj2-modal')) {
          viejo.remove();
        }
      }
    }
    document.body.appendChild(modal);
  }
}

function iniciar() {
  for (const hero of document.querySelectorAll('[data-aj2-hero]')) {
    if (hero.dataset.aj2Listo) continue;
    hero.dataset.aj2Listo = '1';
    iniciarHero(hero);
  }
  for (const sol of document.querySelectorAll('[data-aj2-sol]')) {
    if (sol.dataset.aj2Listo) continue;
    sol.dataset.aj2Listo = '1';
    iniciarSoluciones(sol);
    iniciarAnadir(sol);
  }
  portarModales();
  iniciarModales();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', iniciar, { once: true });
} else {
  iniciar();
}
document.addEventListener('shopify:section:load', iniciar);